import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { INSIGHTS } from "@/data";
import { DiscoveryModal } from "@/components/DiscoveryModal";
import { Seo } from "@/components/Seo";

const openDiscovery = () => window.dispatchEvent(new CustomEvent("open-discovery"));

const Block = ({ block }) => {
  if (block.type === "h") {
    return (
      <h2 className="mt-12 font-serif text-2xl font-semibold tracking-tight text-[#1b1f17] sm:text-3xl">
        {block.text}
      </h2>
    );
  }
  if (block.type === "ul") {
    return (
      <ul className="mt-4 space-y-2 pl-5 text-[#4c5445]">
        {block.items.map((it, i) => (
          <li key={i} className="list-disc leading-relaxed">{it}</li>
        ))}
      </ul>
    );
  }
  return <p className="mt-4 text-base leading-relaxed text-[#4c5445] sm:text-lg">{block.text}</p>;
};

export const Article = () => {
  const { slug } = useParams();
  const post = INSIGHTS.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const others = INSIGHTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div className="grain min-h-screen bg-[#FAFBF7]">
      <Seo
        title={post ? `${post.title} | XSELL Solutions` : "Article Not Found | XSELL Solutions"}
        description={post ? post.excerpt : "Insights on GTM strategy, RevOps and enterprise revenue growth from XSELL Solutions."}
        path={post ? `/insights/${post.slug}` : "/insights"}
        type="article"
      />
      {/* top bar */}
      <header className="sticky top-0 z-50 border-b border-line bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link to="/" data-testid="article-logo" className="flex items-center">
            <img src="/xsell-logo-light.png" alt="XSELL Solutions" className="h-9 w-auto" />
          </Link>
          <button
            type="button"
            onClick={openDiscovery}
            data-testid="article-cta"
            className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-ink-950 transition-colors hover:bg-brand-hover"
          >
            Book a Discovery Call <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </header>

      {!post ? (
        <div className="mx-auto max-w-4xl px-6 py-32 text-center">
          <h1 className="font-serif text-3xl font-semibold text-[#1b1f17]">Article not found</h1>
          <Link to="/#insights" className="mt-6 inline-flex items-center gap-2 text-[#5C8A1E] hover:underline">
            <ArrowLeft className="h-4 w-4" /> Back to Insights
          </Link>
        </div>
      ) : (
        <>
          <article className="mx-auto max-w-3xl px-6 pb-8 pt-14 md:pt-20">
            <Link
              to="/#insights"
              data-testid="article-back"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#5C8A1E] transition-colors hover:text-[#4a7418]"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Insights
            </Link>

            <div className="mt-8 flex items-center gap-3">
              <span className="rounded-full bg-[#5C8A1E]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#5C8A1E]">
                {post.tag}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-[#828c75]">
                <Clock className="h-3.5 w-3.5" /> {post.read}
              </span>
            </div>

            <h1 className="mt-5 font-serif text-4xl font-semibold leading-tight tracking-tight text-[#1b1f17] sm:text-5xl">
              {post.title}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-[#586150]">{post.excerpt}</p>

            <div className="my-10 h-px w-full bg-line" />

            <div data-testid="article-content">
              {post.content.map((block, i) => (
                <Block key={i} block={block} />
              ))}
            </div>
          </article>

          {/* CTA band */}
          <section className="mx-auto mt-8 max-w-3xl px-6">
            <div className="rounded-3xl border border-brand/30 bg-[#F4F6F0] p-8 text-center md:p-12">
              <h3 className="font-serif text-2xl font-semibold text-[#1b1f17] sm:text-3xl">
                Ready to put this into practice?
              </h3>
              <p className="mx-auto mt-3 max-w-lg text-[#586150]">
                Book a 30-minute GTM Strategy Audit and get a clear diagnosis of your revenue leaks.
              </p>
              <a
                href="/#contact"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-ink-950 transition-colors hover:bg-brand-hover"
              >
                Book a Discovery Call <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </section>

          {/* related */}
          {others.length > 0 && (
            <section className="mx-auto max-w-3xl px-6 py-16">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#5C8A1E]">Keep reading</p>
              <div className="mt-6 grid gap-6 sm:grid-cols-2">
                {others.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/insights/${p.slug}`}
                    data-testid={`related-${p.slug}`}
                    className="group rounded-2xl border border-line bg-white p-6 shadow-[0_6px_28px_rgba(28,38,18,0.06)] transition-transform duration-300 hover:-translate-y-1 hover:border-brand/40"
                  >
                    <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#5C8A1E]">{p.tag}</span>
                    <h4 className="mt-3 font-serif text-lg font-medium leading-snug text-[#1b1f17]">{p.title}</h4>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <footer className="border-t border-line py-8">
        <div className="mx-auto max-w-4xl px-6 text-center text-xs text-[#8e9882]">
          © {new Date().getFullYear()} XSELL Solutions — GTM & Revenue Consulting.
        </div>
      </footer>

      <DiscoveryModal />
    </div>
  );
};
